import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, UserPlus, Check } from 'lucide-react';
import { getTeamMembers } from '../../services/teamService';
import type { TeamMember } from '../../types/team';
import { getUserColorFromId, getUserInitial } from '../../utils/userColor';

interface SelectedUser {
  id: string;
  nickname: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface AssignTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  todoTitle: string;
  teamId: string;
  currentAssignedUserId?: string | null;
  onSelect: (userId: string | null, user: SelectedUser | null) => void;
}

export const AssignTaskModal: React.FC<AssignTaskModalProps> = ({
  isOpen,
  onClose,
  todoTitle,
  teamId,
  currentAssignedUserId,
  onSelect,
}) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    currentAssignedUserId || null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadMembers();
      setSelectedUserId(currentAssignedUserId || null);
    }
  }, [isOpen, teamId, currentAssignedUserId]);

  const loadMembers = async () => {
    try {
      setIsLoading(true);
      const data = await getTeamMembers(teamId);
      setMembers(data);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('メンバーの読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (selectedUserId === currentAssignedUserId) {
      onClose();
      return;
    }

    // 選択されたユーザー情報を取得（DBには保存しない）
    const selectedMember = selectedUserId
      ? members.find(m => m.user_id === selectedUserId)
      : null;
    const selectedUserInfo: SelectedUser | null = selectedMember
      ? {
          id: selectedMember.user_id,
          nickname: selectedMember.nickname || null,
          email: selectedMember.email || null,
          avatar_url: selectedMember.avatar_url || null,
        }
      : null;

    // 親コンポーネントに選択結果を返す（保存は親側で行う）
    onSelect(selectedUserId, selectedUserInfo);
    onClose();
  };

  const handleClose = () => {
    setSelectedUserId(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4 animate-fade-slide-in">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl animate-scale-in">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                担当者を設定
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {todoTitle}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors tap-highlight"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  担当者を選択
                </label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {/* 未割り当てオプション */}
                  <button
                    type="button"
                    onClick={() => setSelectedUserId(null)}
                    className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                      selectedUserId === null
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <UserPlus className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            未割り当て
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            担当者を設定しない
                          </p>
                        </div>
                      </div>
                      {selectedUserId === null && (
                        <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      )}
                    </div>
                  </button>

                  {/* メンバー一覧 */}
                  {members.map((member) => (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => setSelectedUserId(member.user_id)}
                      className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                        selectedUserId === member.user_id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getUserColorFromId(member.user_id)} flex items-center justify-center`}>
                            {member.avatar_url ? (
                              <img
                                src={member.avatar_url}
                                alt={member.nickname || member.email || 'User'}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-white font-semibold text-sm">
                                {getUserInitial(member.nickname, member.email)}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {member.nickname || 'Unknown'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {member.email}
                            </p>
                          </div>
                        </div>
                        {selectedUserId === member.user_id && (
                          <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600
                    text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700
                    transition-colors tap-highlight font-medium"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={selectedUserId === currentAssignedUserId}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg
                    hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                    flex items-center justify-center gap-2 tap-highlight font-medium"
                >
                  選択
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>,
    document.body
  );
};
